/**
 * POSWelcome - Peace of Sleep Onboarding Wizard
 *
 * A beautiful, step-by-step onboarding experience for hotel managers.
 * Validates invite token, sends magic link, collects user details, and creates their account.
 *
 * URL: pos.cleanmybed.com/welcome?token=xxx
 *
 * Steps:
 * 1. Token validation (automatic)
 * 2. Welcome screen with property info → sends magic link from frontend
 * 3. Check email screen (wait for magic link click)
 * 4. Set your details (name) - arrives here after magic link
 * 5. Create password
 * 6. Success + auto-login
 *
 * IMPORTANT: Magic link is sent FROM THE FRONTEND (not edge function).
 * This mirrors the working Onboard.jsx pattern.
 */

import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { useInstallPrompt } from '../../../hooks/useInstallPrompt';
import '../pos-theme.css';
import './pos-welcome.css';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;

type WelcomeStep = 'loading' | 'invalid' | 'welcome' | 'linkSent' | 'details' | 'password' | 'install' | 'success';

interface POSInvite {
  email: string;
  property_name: string;
  access_level: string;
  property_slug?: string;
}

interface AcceptInviteResponse {
  valid?: boolean;
  success?: boolean;
  error?: string;
  invite?: POSInvite;
  access_token?: string;
  refresh_token?: string;
  property?: { slug?: string };
}

function POSWelcome() {
  const { supabase, user: _user, loading: _authLoading } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isInstalled, isInstallable, promptInstall } = useInstallPrompt();

  // Support both ?token=xxx (old) and ?invite=xxx (new magic link flow)
  const token = searchParams.get('token') || searchParams.get('invite');
  const fromMagic = searchParams.get('from') === 'magic';
  const previewStep = searchParams.get('preview');
  const isPreviewMode = !!previewStep;

  // Device detection
  const [deviceType, setDeviceType] = useState<'desktop' | 'ios' | 'android'>('desktop');
  useEffect(() => {
    const ua = navigator.userAgent.toLowerCase();
    if (/iphone|ipad|ipod/.test(ua)) {
      setDeviceType('ios');
    } else if (/android/.test(ua)) {
      setDeviceType('android');
    } else {
      setDeviceType('desktop');
    }
  }, []);

  // State
  const [step, setStep] = useState<WelcomeStep>('loading');
  const [invite, setInvite] = useState<POSInvite | null>(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    password: '',
    confirmPassword: ''
  });
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sendingLink, setSendingLink] = useState(false);
  const [installing, setInstalling] = useState(false);
  const [installConfirmed, setInstallConfirmed] = useState(false);

  // Preview mode step mapping
  const previewSteps = useMemo<Record<string, WelcomeStep>>(() => ({
    '1': 'welcome',
    '2': 'linkSent',
    '3': 'details',
    '4': 'password',
    '5': 'install',
    '6': 'success'
  }), []);

  // Preview mode - set step and mock data
  useEffect(() => {
    if (isPreviewMode && previewStep) {
      const stepName: WelcomeStep = previewSteps[previewStep] || 'welcome';
      setStep(stepName);
      // Mock invite data for preview
      setInvite({
        email: 'manager@hotel-example.com',
        property_name: 'Grand Hotel Example',
        access_level: 'Manager',
        property_slug: 'grand-hotel'
      });
    }
  }, [isPreviewMode, previewStep, previewSteps]);

  // Validate token on mount (skip in preview mode)
  useEffect(() => {
    if (isPreviewMode) return;
    if (!token) {
      setStep('invalid');
      return;
    }
    validateToken();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- validate on mount/token change, not when validateToken reference changes
  }, [token, isPreviewMode]);

  async function validateToken() {
    try {
      const response = await fetch(`${SUPABASE_URL}/functions/v1/pos-accept-invite?token=${token}`);
      const data: AcceptInviteResponse = await response.json();

      if (!response.ok || !data.valid) {
        setError(data.error || 'Invalid or expired invitation');
        setStep('invalid');
        return;
      }

      setInvite(data.invite || null);

      // Always show welcome screen first (branded experience)
      // fromMagic just means they skip the email step
      setStep('welcome');
    } catch (_err: unknown) {
      setError('Failed to validate invitation. Please try again.');
      setStep('invalid');
    }
  }

  /**
   * Send magic link from frontend (mirrors Onboard.jsx pattern)
   * This is the KEY difference - we send the OTP from frontend, not edge function
   */
  async function handleSendMagicLink() {
    if (!invite?.email) {
      setError('No email found for this invitation');
      return;
    }

    setSendingLink(true);
    setError('');

    try {
      // Build redirect URL - return to this page with token + from=magic
      const redirectUrl = `${window.location.origin}/pos/welcome?token=${token}&from=magic`;

      const { error: otpError } = await supabase!.auth.signInWithOtp({
        email: invite.email,
        options: {
          emailRedirectTo: redirectUrl,
          shouldCreateUser: true // Create auth user if doesn't exist
        }
      });

      if (otpError) {
        setError(otpError.message || 'Failed to send verification email');
        setSendingLink(false);
        return;
      }

      setStep('linkSent');
    } catch (_err: unknown) {
      setError('Failed to send verification email. Please try again.');
    } finally {
      setSendingLink(false);
    }
  }

  async function handleSubmit() {
    // Validate form
    if (!formData.firstName.trim() || !formData.lastName.trim()) {
      setError('Please enter your first and last name');
      return;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      // Get current session for auth header
      const { data: sessionData } = await supabase!.auth.getSession();
      const accessToken = sessionData?.session?.access_token;

      const response = await fetch(`${SUPABASE_URL}/functions/v1/pos-accept-invite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(accessToken && { 'Authorization': `Bearer ${accessToken}` })
        },
        body: JSON.stringify({
          token,
          first_name: formData.firstName.trim(),
          last_name: formData.lastName.trim(),
          password: formData.password
        })
      });

      const data: AcceptInviteResponse = await response.json();

      if (!response.ok || !data.success) {
        setError(data.error || 'Failed to complete setup');
        setSubmitting(false);
        return;
      }

      // Store session tokens for auto-login (if edge function returns new session)
      if (data.access_token) {
        await supabase!.auth.setSession({
          access_token: data.access_token,
          refresh_token: data.refresh_token || ''
        });
      }

      // Store property info for redirect
      setInvite(prev => prev ? ({
        ...prev,
        property_slug: data.property?.slug
      }) : prev);

      // Go to install step (or skip to success if already installed)
      if (isInstalled) {
        setStep('success');
      } else {
        setStep('install');
      }
    } catch (_err: unknown) {
      setError('An unexpected error occurred. Please try again.');
      setSubmitting(false);
    }
  }

  // Handle native install prompt (Android/Chrome)
  async function handleNativeInstall() {
    setInstalling(true);
    try {
      const result = await promptInstall();
      if (result) {
        setInstallConfirmed(true);
        setTimeout(() => setStep('success'), 1500);
      }
    } catch {
      // no-op
    } finally {
      setInstalling(false);
    }
  }

  // Mark as installed manually (iOS/fallback)
  function handleConfirmInstall() {
    setInstallConfirmed(true);
    setTimeout(() => setStep('success'), 1000);
  }

  // Skip install step
  function handleSkipInstall() {
    setStep('success');
  }

  function handleContinue() {
    // Redirect to their property dashboard
    if (invite?.property_slug) {
      navigate(`/pos/${invite.property_slug}`);
    } else {
      navigate('/pos');
    }
  }

  // ========== Render Helpers ==========

  // Preview banner for navigating between steps
  const PreviewBanner = () => {
    if (!isPreviewMode) return null;
    const currentStep = Object.entries(previewSteps).find(([, name]) => name === step)?.[0] || '1';
    return (
      <div className="pos-preview-banner">
        <span>Preview Mode</span>
        <div className="pos-preview-nav">
          {Object.entries(previewSteps).map(([num, name]) => (
            <button
              key={num}
              className={currentStep === num ? 'active' : ''}
              onClick={() => setStep(name)}
              title={name}
            >
              {num}
            </button>
          ))}
        </div>
      </div>
    );
  };

  // ========== Render Steps ==========

  // Loading
  if (step === 'loading') {
    return (
      <div className="pos-welcome">
        <div className="pos-welcome-content">
          <div className="pos-welcome-loading">
            <div className="pos-welcome-spinner" />
            <p>Validating your invitation...</p>
          </div>
        </div>
      </div>
    );
  }

  // Invalid Token
  if (step === 'invalid') {
    return (
      <div className="pos-welcome">
        <div className="pos-welcome-content">
          <div className="pos-welcome-header">
            <h1 className="pos-welcome-brand">PEACE OF SLEEP</h1>
          </div>
          <div className="pos-welcome-card">
            <div className="pos-welcome-error-icon">!</div>
            <h2>Invalid Invitation</h2>
            <p>{error || 'This invitation link is invalid or has expired.'}</p>
            <p className="pos-welcome-help">
              Please contact your property administrator for a new invitation.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Welcome Step - Shows invite info, sends magic link on click
  if (step === 'welcome') {
    return (
      <div className="pos-welcome">
        <div className="pos-welcome-content">
          <div className="pos-welcome-header">
            <h1 className="pos-welcome-brand">PEACE OF SLEEP</h1>
            <p className="pos-welcome-tagline">The Quiet Standard Of Trust</p>
          </div>

          <div className="pos-welcome-card">
            <PreviewBanner />
            <div className="pos-welcome-icon">✨</div>
            <h2>Welcome to {invite?.property_name}</h2>
            <p>
              You&apos;ve been invited to manage {invite?.property_name} on the
              Peace of Sleep hospitality portal.
            </p>
            <div className="pos-welcome-invite-info">
              <div className="pos-welcome-info-row">
                <span className="pos-welcome-info-label">Email</span>
                <span className="pos-welcome-info-value">{invite?.email}</span>
              </div>
              <div className="pos-welcome-info-row">
                <span className="pos-welcome-info-label">Access Level</span>
                <span className="pos-welcome-info-value pos-welcome-access-badge">
                  {invite?.access_level}
                </span>
              </div>
            </div>

            {error && (
              <div className="pos-welcome-error">{error}</div>
            )}

            {fromMagic ? (
              <>
                <button
                  className="pos-welcome-btn pos-welcome-btn-primary"
                  onClick={() => setStep('details')}
                >
                  Continue
                </button>
              </>
            ) : (
              <>
                <button
                  className="pos-welcome-btn pos-welcome-btn-primary"
                  onClick={handleSendMagicLink}
                  disabled={sendingLink}
                >
                  {sendingLink ? 'Sending...' : 'Get Started'}
                </button>
                <p className="pos-welcome-note">
                  We&apos;ll send a verification link to {invite?.email}
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Link Sent Step - Waiting for user to click email link
  if (step === 'linkSent') {
    return (
      <div className="pos-welcome">
        <div className="pos-welcome-content">
          <div className="pos-welcome-header">
            <h1 className="pos-welcome-brand">PEACE OF SLEEP</h1>
          </div>

          <div className="pos-welcome-card">
            <PreviewBanner />
            <div className="pos-welcome-icon">📧</div>
            <h2>Check Your Email</h2>
            <p>
              We&apos;ve sent a verification link to <strong>{invite?.email}</strong>
            </p>
            <p className="pos-welcome-help">
              Click the link in the email to continue setting up your account.
              The link will expire in 1 hour.
            </p>

            <div className="pos-welcome-divider" />

            <p className="pos-welcome-note">
              Didn&apos;t receive the email? Check your spam folder or
            </p>
            <button
              className="pos-welcome-btn pos-welcome-btn-secondary"
              onClick={handleSendMagicLink}
              disabled={sendingLink}
            >
              {sendingLink ? 'Sending...' : 'Resend Email'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Details Step - Collect name (user arrives here after magic link)
  if (step === 'details') {
    return (
      <div className="pos-welcome">
        <div className="pos-welcome-content">
          <div className="pos-welcome-header">
            <h1 className="pos-welcome-brand">PEACE OF SLEEP</h1>
          </div>

          <div className="pos-welcome-card">
            <PreviewBanner />
            <div className="pos-welcome-step-indicator">
              <span className="pos-step active">1</span>
              <span className="pos-step-line" />
              <span className="pos-step">2</span>
            </div>

            <h2>Your Details</h2>
            <p>Let us know who you are.</p>

            {error && (
              <div className="pos-welcome-error">{error}</div>
            )}

            <div className="pos-welcome-form">
              <div className="pos-welcome-field">
                <label className="pos-welcome-label">First Name</label>
                <input
                  type="text"
                  className="pos-welcome-input"
                  placeholder="Enter your first name"
                  value={formData.firstName}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, firstName: e.target.value }));
                    setError('');
                  }}
                  autoFocus
                />
              </div>

              <div className="pos-welcome-field">
                <label className="pos-welcome-label">Last Name</label>
                <input
                  type="text"
                  className="pos-welcome-input"
                  placeholder="Enter your last name"
                  value={formData.lastName}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, lastName: e.target.value }));
                    setError('');
                  }}
                />
              </div>
            </div>

            <div className="pos-welcome-actions">
              <button
                className="pos-welcome-btn pos-welcome-btn-primary"
                onClick={() => {
                  if (!formData.firstName.trim() || !formData.lastName.trim()) {
                    setError('Please enter your name');
                    return;
                  }
                  setError('');
                  setStep('password');
                }}
              >
                Continue
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Password Step
  if (step === 'password') {
    return (
      <div className="pos-welcome">
        <div className="pos-welcome-content">
          <div className="pos-welcome-header">
            <h1 className="pos-welcome-brand">PEACE OF SLEEP</h1>
          </div>

          <div className="pos-welcome-card">
            <PreviewBanner />
            <div className="pos-welcome-step-indicator">
              <span className="pos-step completed">✓</span>
              <span className="pos-step-line completed" />
              <span className="pos-step active">2</span>
            </div>

            <h2>Create Password</h2>
            <p>Choose a secure password for your account.</p>

            {error && (
              <div className="pos-welcome-error">{error}</div>
            )}

            <div className="pos-welcome-form">
              <div className="pos-welcome-field">
                <label className="pos-welcome-label">Password</label>
                <input
                  type="password"
                  className="pos-welcome-input"
                  placeholder="Minimum 8 characters"
                  value={formData.password}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, password: e.target.value }));
                    setError('');
                  }}
                  autoFocus
                />
              </div>

              <div className="pos-welcome-field">
                <label className="pos-welcome-label">Confirm Password</label>
                <input
                  type="password"
                  className="pos-welcome-input"
                  placeholder="Re-enter your password"
                  value={formData.confirmPassword}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, confirmPassword: e.target.value }));
                    setError('');
                  }}
                />
              </div>
            </div>

            <div className="pos-welcome-actions">
              <button
                className="pos-welcome-btn pos-welcome-btn-secondary"
                onClick={() => setStep('details')}
                disabled={submitting}
              >
                Back
              </button>
              <button
                className="pos-welcome-btn pos-welcome-btn-primary"
                onClick={handleSubmit}
                disabled={submitting}
              >
                {submitting ? 'Creating Account...' : 'Create Account'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Install Step
  if (step === 'install') {
    return (
      <div className="pos-welcome">
        <div className="pos-welcome-content">
          <div className="pos-welcome-header">
            <h1 className="pos-welcome-brand">PEACE OF SLEEP</h1>
          </div>

          <div className="pos-welcome-card">
            <PreviewBanner />
            {/* Success state after install */}
            {installConfirmed ? (
              <div className="pos-install-success">
                <div className="pos-install-success-icon">
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                </div>
                <h2>App Installed!</h2>
                <p>Peace of Sleep is now on your device.</p>
              </div>
            ) : (
              <>
                <div className="pos-welcome-step-indicator">
                  <span className="pos-step completed">✓</span>
                  <span className="pos-step-line completed" />
                  <span className="pos-step completed">✓</span>
                  <span className="pos-step-line completed" />
                  <span className="pos-step active">3</span>
                </div>

                <div className="pos-install-icon">📱</div>
                <h2>Add to Home Screen</h2>
                <p>Get quick access to your property dashboard — just like a regular app.</p>

                {/* Android with native install */}
                {deviceType === 'android' && isInstallable && (
                  <div className="pos-install-native">
                    <button
                      className="pos-install-btn"
                      onClick={handleNativeInstall}
                      disabled={installing}
                    >
                      {installing ? (
                        <>
                          <span className="pos-install-spinner" />
                          Installing...
                        </>
                      ) : (
                        <>
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                            <polyline points="7 10 12 15 17 10"/>
                            <line x1="12" y1="15" x2="12" y2="3"/>
                          </svg>
                          Install App
                        </>
                      )}
                    </button>
                  </div>
                )}

                {/* Android fallback instructions */}
                {deviceType === 'android' && !isInstallable && (
                  <div className="pos-install-instructions">
                    <div className="pos-install-step">
                      <span className="pos-install-step-num">1</span>
                      <p>Tap the <strong>menu</strong> (⋮) in the top right</p>
                    </div>
                    <div className="pos-install-step">
                      <span className="pos-install-step-num">2</span>
                      <p>Tap <strong>&quot;Install App&quot;</strong></p>
                    </div>
                    <div className="pos-install-step">
                      <span className="pos-install-step-num">3</span>
                      <p>Tap <strong>&quot;Install&quot;</strong></p>
                    </div>
                  </div>
                )}

                {/* iOS instructions */}
                {deviceType === 'ios' && (
                  <div className="pos-install-instructions">
                    <div className="pos-install-ios-demo">
                      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#BD982F" strokeWidth="2">
                        <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/>
                        <polyline points="16 6 12 2 8 6"/>
                        <line x1="12" y1="2" x2="12" y2="15"/>
                      </svg>
                      <span>Look for this icon</span>
                    </div>
                    <div className="pos-install-step">
                      <span className="pos-install-step-num">1</span>
                      <p>Tap <strong>Share</strong> at the bottom of Safari</p>
                    </div>
                    <div className="pos-install-step">
                      <span className="pos-install-step-num">2</span>
                      <p>Tap <strong>&quot;Add to Home Screen&quot;</strong></p>
                    </div>
                    <div className="pos-install-step">
                      <span className="pos-install-step-num">3</span>
                      <p>Tap <strong>&quot;Add&quot;</strong></p>
                    </div>
                  </div>
                )}

                {/* Desktop message */}
                {deviceType === 'desktop' && (
                  <div className="pos-install-desktop">
                    <p>Open this page on your <strong>phone</strong> to add to your home screen.</p>
                    <p className="pos-install-desktop-hint">You can also bookmark this page for quick access on desktop.</p>
                  </div>
                )}

                <div className="pos-welcome-actions">
                  {(deviceType === 'ios' || (deviceType === 'android' && !isInstallable)) && (
                    <button
                      className="pos-welcome-btn pos-welcome-btn-primary"
                      onClick={handleConfirmInstall}
                    >
                      I&apos;ve Added It
                    </button>
                  )}
                  <button
                    className="pos-welcome-btn pos-welcome-btn-secondary"
                    onClick={handleSkipInstall}
                  >
                    {deviceType === 'desktop' ? 'Continue' : 'Skip for Now'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Success Step
  if (step === 'success') {
    return (
      <div className="pos-welcome">
        <div className="pos-welcome-content">
          <div className="pos-welcome-header">
            <h1 className="pos-welcome-brand">PEACE OF SLEEP</h1>
          </div>

          <div className="pos-welcome-card pos-welcome-success">
            <PreviewBanner />
            <div className="pos-welcome-success-icon">✓</div>
            <h2>Welcome Aboard!</h2>
            <p>
              Your account has been created successfully.
              You now have access to manage {invite?.property_name}.
            </p>

            <button
              className="pos-welcome-btn pos-welcome-btn-primary"
              onClick={handleContinue}
            >
              Go to Dashboard
            </button>
          </div>
        </div>

        <div className="pos-welcome-confetti" />
      </div>
    );
  }

  // Default / Error
  return (
    <div className="pos-welcome">
      <div className="pos-welcome-content">
        <div className="pos-welcome-card">
          <h2>Something went wrong</h2>
          <p>Please try again or contact support.</p>
        </div>
      </div>
    </div>
  );
}

export default POSWelcome;
